import { useEffect, useRef } from "react";
import { getAnalyser } from "@/lib/audio";

/**
 * Audio-reactive waveform visualizer.
 * If no real audio is playing, renders an animated synthetic wave so the hero stays alive.
 */
export default function Visualizer({ height = 180, color = "#FF003C", color2 = "#8B00FF", style = "wave" }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const tRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = canvas.clientWidth;
    let h = canvas.clientHeight;
    canvas.width = width * 2;
    canvas.height = h * 2;
    ctx.scale(2, 2);

    const handle = () => {
      width = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = width * 2;
      canvas.height = h * 2;
      ctx.scale(2, 2);
    };
    window.addEventListener("resize", handle);

    let analyser = null;
    let dataArr = null;
    try {
      analyser = getAnalyser();
      dataArr = new Uint8Array(analyser.fftSize);
    } catch {
      analyser = null;
    }

    const loop = () => {
      tRef.current += 0.02;
      ctx.clearRect(0, 0, width, h);

      // Background grid lines
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 32) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      for (let y = 0; y < h; y += 32) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
      }

      let useReal = false;
      let avg = 0;
      if (analyser) {
        analyser.getByteTimeDomainData(dataArr);
        for (let i = 0; i < dataArr.length; i += 16) avg += Math.abs(dataArr[i] - 128);
        avg /= dataArr.length / 16;
        useReal = avg > 1.5;
      }

      if (style === "bars") {
        const bars = 64;
        const bw = width / bars;
        for (let i = 0; i < bars; i++) {
          let v;
          if (useReal && analyser) {
            const idx = Math.floor((i / bars) * dataArr.length);
            v = Math.abs(dataArr[idx] - 128) / 128;
          } else {
            v = 0.35 + 0.4 * Math.abs(Math.sin(tRef.current * 1.7 + i * 0.32))
                + 0.25 * Math.abs(Math.sin(tRef.current * 0.6 + i * 0.13));
          }
          const bh = v * h * 0.9;
          const grad = ctx.createLinearGradient(0, h, 0, h - bh);
          grad.addColorStop(0, color);
          grad.addColorStop(1, color2);
          ctx.fillStyle = grad;
          ctx.fillRect(i * bw + 2, h - bh, bw - 4, bh);
        }
      } else {
        // Wave style
        ctx.lineWidth = 2;
        ctx.shadowBlur = 24;
        ctx.shadowColor = color;
        ctx.strokeStyle = color;
        ctx.beginPath();
        for (let x = 0; x < width; x++) {
          let y;
          if (useReal && analyser) {
            const idx = Math.floor((x / width) * dataArr.length);
            y = ((dataArr[idx] - 128) / 128) * (h / 2.2) + h / 2;
          } else {
            y = h / 2
              + Math.sin(x * 0.018 + tRef.current * 1.6) * 22
              + Math.sin(x * 0.04 + tRef.current * 2.4) * 12
              + Math.sin(x * 0.008 + tRef.current * 0.8) * 30;
          }
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Second offset purple line
        ctx.strokeStyle = color2;
        ctx.globalAlpha = 0.55;
        ctx.beginPath();
        for (let x = 0; x < width; x++) {
          const y = h / 2
            + Math.sin(x * 0.018 + tRef.current * 1.6 + 0.8) * 22
            + Math.sin(x * 0.04 + tRef.current * 2.4 + 0.6) * 12;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", handle);
    };
  }, [color, color2, style]);

  return (
    <div className="relative w-full" style={{ height }} data-testid="audio-visualizer">
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
